// src/views/EventDetail.jsx
import React, {useState, useEffect} from "react";
import {Container, Row, Col, Breadcrumb, Card, Spinner, Alert, Badge} from "react-bootstrap";
import {Link, useParams, useNavigate} from "react-router-dom";
import {
    HomeOutlined,
    CalendarOutlined,
    FileOutlined,
    TeamOutlined,
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    EyeInvisibleOutlined,
    DownloadOutlined,
} from "@ant-design/icons";
import {
    Button,
    Descriptions,
    Tag,
    Avatar,
    Divider,
    Modal,
    message,
    Tooltip,
    List,
    Space,
    Statistic,
} from "antd";
import {getEventDetailWithStatus, markEventAsRead, markEventAsUnread, deleteEvent, markTaskStatus} from "@/api/eventApi";
import {decodeToken} from "@/utils/tokenCookies";
import "./EventDetail.css";

const EventDetail = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const token = decodeToken();
    const isAdmin = token?.roles?.includes("admin") || token?.roles?.includes("superadmin");
    const canManageTasks = event?.can_manage_tasks ?? isAdmin;

    useEffect(() => {
        fetchEventDetail();
    }, [id]);

    const fetchEventDetail = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getEventDetailWithStatus(id);
            setEvent(data);

            // Automatically mark as read when viewing (only if permitted)
            if (data.can_mark_read && !data.is_read) {
                try {
                    await markEventAsRead(id);
                    setEvent((prev) =>
                        prev
                            ? {...prev, is_read: true, read_at: new Date().toISOString()}
                            : prev
                    );
                } catch (markErr) {
                    console.warn("Mark read failed:", markErr);
                }
            }
        } catch (err) {
            console.error("Error fetching event:", err);
            setError(err.response?.data?.message || "Event ma'lumotlarini yuklashda xatolik");
            message.error("Event topilmadi");
        } finally {
            setLoading(false);
        }
    };

    const handleToggleRead = async () => {
        if (!event?.can_mark_read) {
            return;
        }
        try {
            if (event.is_read) {
                await markEventAsUnread(id);
                message.success("Event o'qilmagan deb belgilandi");
                setEvent((prev) => ({...prev, is_read: false, read_at: null}));
            } else {
                await markEventAsRead(id);
                message.success("Event o'qilgan deb belgilandi");
                setEvent((prev) => ({...prev, is_read: true, read_at: new Date().toISOString()}));
            }
        } catch (err) {
            message.error("Xatolik yuz berdi");
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await deleteEvent(id);
            message.success("Event muvaffaqiyatli o'chirildi");
            navigate("/events/all");
        } catch (err) {
            message.error(err.response?.data?.message || "Event o'chirishda xatolik");
        } finally {
            setDeleting(false);
            setDeleteModalVisible(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        return `${day}.${month}.${year} ${hours}:${minutes}`;
    };


    const getStatusBadge = (isRead) => {
        return isRead ? (
            <Badge bg="success">O'qilgan</Badge>
        ) : (
            <Badge bg="primary">Yangi</Badge>
        );
    };

    const getEventStatusBadge = (status) => {
        const statusConfig = {
            upcoming: {color: "blue", text: "Kelgusi"},
            ongoing: {color: "orange", text: "Davom etmoqda"},
            completed: {color: "green", text: "Yakunlangan"},
            overdue: {color: "red", text: "Muddati o'tgan"},
        };
        const config = statusConfig[status] || statusConfig.upcoming;
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    const getTaskStatusTag = (status) => {
        return status === "completed" ? (
            <Tag color="green">Bajarildi</Tag>
        ) : (
            <Tag color="red">Bajarilmadi</Tag>
        );
    };

    const handleToggleTaskStatus = async (taskId, userId, currentStatus) => {
        if (!canManageTasks) {
            return;
        }
        const newStatus = currentStatus === "completed" ? "not_completed" : "completed";
        try {
            await markTaskStatus(id, taskId, {user_id: userId, status: newStatus});
            message.success("Status o'zgartirildi");
            fetchEventDetail(); // Refresh data
        } catch (err) {
            message.error(err.response?.data?.message || "Xatolik yuz berdi");
        }
    };

    if (loading) {
        return (
            <Container fluid className="p-4 text-center" style={{minHeight: "60vh"}}>
                <Spinner animation="border" variant="primary" style={{marginTop: "100px"}}/>
                <p className="mt-3">Event yuklanmoqda...</p>
            </Container>
        );
    }

    if (error) {
        return (
            <Container fluid className="p-4">
                <Alert variant="danger">
                    <Alert.Heading>Xatolik yuz berdi</Alert.Heading>
                    <p>{error}</p>
                    <Button onClick={() => navigate(-1)}>Orqaga</Button>
                </Alert>
            </Container>
        );
    }

    if (!event) {
        return null;
    }

    return (
        <Container fluid className="p-4">
            {/* Breadcrumb */}
            <Breadcrumb>
                <Breadcrumb.Item linkAs={Link} linkProps={{to: "/"}}>
                    <HomeOutlined/>
                </Breadcrumb.Item>
                <Breadcrumb.Item linkAs={Link} linkProps={{to: isAdmin ? "/events/all" : "/events"}}>
                    {isAdmin ? "Barcha tadbirlar" : "Mening tadbirlarim"}
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{event.name}</Breadcrumb.Item>
            </Breadcrumb>

            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 mt-3">
                <div>
                    <h2 className="mb-2">{event.name}</h2>
                    <Space>
                        {event.can_mark_read && getStatusBadge(event.is_read)}
                        {event.event_status && getEventStatusBadge(event.event_status)}
                        <Tag color="blue" icon={<CalendarOutlined/>}>
                            {formatDate(event.datetime)}
                        </Tag>
                    </Space>
                </div>
                <Space>
                    {/*{event.can_mark_read && (*/}
                    {/*    <Tooltip title={event.is_read ? "O'qilmagan deb belgilash" : "O'qilgan deb belgilash"}>*/}
                    {/*        <Button*/}
                    {/*            icon={event.is_read ? <EyeOutlined/> : <EyeInvisibleOutlined/>}*/}
                    {/*            onClick={handleToggleRead}*/}
                    {/*        >*/}
                    {/*            {event.is_read ? "O'qilgan" : "O'qilmagan"}*/}
                    {/*        </Button>*/}
                    {/*    </Tooltip>*/}
                    {/*)}*/}
                    {isAdmin && (
                        <>
                            <Button
                                icon={<EditOutlined/>}
                                onClick={() => navigate(`/events/edit/${id}`)}
                            >
                                Tahrirlash
                            </Button>
                            <Button
                                danger
                                icon={<DeleteOutlined/>}
                                onClick={() => setDeleteModalVisible(true)}
                            >
                                O'chirish
                            </Button>
                        </>
                    )}
                </Space>
            </div>

            <Row className="g-4">
                {/* Main Content */}
                <Col xs={12} lg={8}>
                    {/* Event Info */}
                    <Card className="mb-4">
                        <Card.Body>
                            <h5 className="mb-3">Event ma'lumotlari</h5>
                            <Descriptions column={1} bordered>
                                <Descriptions.Item label="Event nomi">{event.name}</Descriptions.Item>
                                <Descriptions.Item label="Sana va vaqt">
                                    <CalendarOutlined className="me-2"/>
                                    {formatDate(event.datetime)}
                                </Descriptions.Item>
                                {event.file && (
                                    <Descriptions.Item label="Biriktirilgan fayl">
                                        <a href={event.file} target="_blank" rel="noopener noreferrer">
                                            <Button
                                                type="link"
                                                icon={<DownloadOutlined/>}
                                                style={{padding: 0}}
                                            >
                                                Faylni yuklab olish
                                            </Button>
                                        </a>
                                    </Descriptions.Item>
                                )}
                                {event.read_at && (
                                    <Descriptions.Item label="O'qilgan vaqt">
                                        <CalendarOutlined className="me-2"/>
                                        {formatDate(event.read_at)}
                                    </Descriptions.Item>
                                )}
                            </Descriptions>
                        </Card.Body>
                    </Card>

                    {/* Tasks */}
                    <Card>
                        <Card.Body>
                            <h5 className="mb-3">
                                Topshiriqlar ({event.tasks?.length || 0})
                            </h5>
                            {event.tasks && event.tasks.length > 0 ? (
                                <List
                                    dataSource={event.tasks}
                                    renderItem={(task, index) => (
                                        <List.Item className="task-list-item">
                                            <div className="task-item-content">
                                                <div className="task-header">
                                                    <h6 className="task-title">
                                                        {index + 1}. {task.title}
                                                    </h6>
                                                </div>
                                                {task.description && (
                                                    <p className="task-description">{task.description}</p>
                                                )}

                                                {/* User Statuses */}
                                                {task.user_statuses && task.user_statuses.length > 0 && (
                                                    <div style={{marginTop: "12px", marginBottom: "12px"}}>
                                                        <p className="text-muted mb-2">
                                                            <strong>Bajarilish holati:</strong>
                                                        </p>
                                                        {task.user_statuses.map((userStatus) => (
                                                            <div
                                                                key={userStatus.id}
                                                                style={{
                                                                    display: "flex",
                                                                    justifyContent: "space-between",
                                                                    alignItems: "center",
                                                                    padding: "8px 12px",
                                                                    marginBottom: "8px",
                                                                    backgroundColor: "#fafafa",
                                                                    borderRadius: "4px",
                                                                    border: "1px solid #e8e8e8",
                                                                }}
                                                            >
                                                                <div style={{display: "flex", alignItems: "center", gap: "8px"}}>
                                                                    <span>{userStatus.user_name}</span>
                                                                    {getTaskStatusTag(userStatus.status)}
                                                                </div>
                                                                {canManageTasks && event.event_status !== "upcoming" && (
                                                                    <Button
                                                                        size="small"
                                                                        type={userStatus.status === "completed" ? "default" : "primary"}
                                                                        onClick={() =>
                                                                            handleToggleTaskStatus(
                                                                                task.id,
                                                                                userStatus.user_id,
                                                                                userStatus.status
                                                                            )
                                                                        }
                                                                    >
                                                                        {userStatus.status === "completed"
                                                                            ? "Bajarilmadi"
                                                                            : "Bajarildi"}
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                <div className="task-assignees">
                                                    <TeamOutlined className="me-2"/>
                                                    <span className="text-muted me-2">Javobgarlar:</span>
                                                    <Avatar.Group maxCount={5}>
                                                        {task.assigned_users?.map((user) => (
                                                            <Tooltip key={user.id} title={user.name}>
                                                                <Avatar src={user.picture}>
                                                                    {user.name?.[0]}
                                                                </Avatar>
                                                            </Tooltip>
                                                        ))}
                                                    </Avatar.Group>
                                                    <span className="ms-2 text-muted">
                            ({task.assigned_users?.length || 0} kishi)
                          </span>
                                                </div>
                                            </div>
                                        </List.Item>
                                    )}
                                />
                            ) : (
                                <Alert variant="info">Topshiriqlar mavjud emas</Alert>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                {/* Sidebar */}
                <Col xs={12} lg={4}>
                    {/* Statistics (Admin only) */}
                    {isAdmin && (
                        <Card className="mb-4">
                            <Card.Body>
                                <h5 className="mb-3">Statistika</h5>
                                <Row className="g-3">
                                    {event.total_assigned_users !== undefined && (
                                        <>
                                            <Col xs={12}>
                                                <Statistic
                                                    title="Jami ishtirokchilar"
                                                    value={event.total_assigned_users}
                                                    prefix={<TeamOutlined/>}
                                                />
                                            </Col>
                                            <Col xs={6}>
                                                <Statistic
                                                    title="O'qilgan"
                                                    value={event.read_count || 0}
                                                    valueStyle={{color: "#52c41a"}}
                                                />
                                            </Col>
                                            <Col xs={6}>
                                                <Statistic
                                                    title="O'qilmagan"
                                                    value={event.unread_count || 0}
                                                    valueStyle={{color: "#faad14"}}
                                                />
                                            </Col>
                                        </>
                                    )}
                                    {event.event_status && event.event_status !== "upcoming" && (
                                        <>
                                            <Col xs={12}>
                                                <Divider style={{margin: "12px 0"}}/>
                                                <h6>Task bajarilishi</h6>
                                            </Col>
                                            <Col xs={6}>
                                                <Statistic
                                                    title="Bajarildi"
                                                    value={event.completed_count || 0}
                                                    valueStyle={{color: "#52c41a"}}
                                                />
                                            </Col>
                                            <Col xs={6}>
                                                <Statistic
                                                    title="Bajarilmadi"
                                                    value={event.not_completed_count || 0}
                                                    valueStyle={{color: "#ff4d4f"}}
                                                />
                                            </Col>
                                        </>
                                    )}
                                </Row>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Summary */}
                    <Card>
                        <Card.Body>
                            <h5 className="mb-3">Qisqacha ma'lumot</h5>
                            <div className="summary-item">
                                <strong>Topshiriqlar:</strong>
                                <span>{event.tasks?.length || 0} ta</span>
                            </div>
                            <Divider style={{margin: "12px 0"}}/>
                            <div className="summary-item">
                                <strong>Jami javobgarlar:</strong>
                                <span>
                  {event.tasks?.reduce(
                      (total, task) => total + (task.assigned_users?.length || 0),
                      0
                  ) || 0}{" "}
                                    kishi
                </span>
                            </div>
                            {/*<Divider style={{margin: "12px 0"}}/>*/}
                            {/*<div className="summary-item">*/}
                            {/*    <strong>Status:</strong>*/}
                            {/*    <span>{event.is_read ? "O'qilgan" : "O'qilmagan"}</span>*/}
                            {/*</div>*/}
                            {event.file && (
                                <>
                                    <Divider style={{margin: "12px 0"}}/>
                                    <div className="summary-item">
                                        <strong>Fayl:</strong>
                                        <span>
                      <FileOutlined/> Mavjud
                    </span>
                                    </div>
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Delete Confirmation Modal */}
            <Modal
                title="Eventni o'chirish"
                open={deleteModalVisible}
                onOk={handleDelete}
                onCancel={() => setDeleteModalVisible(false)}
                okText="Ha, o'chirish"
                cancelText="Bekor qilish"
                okButtonProps={{danger: true, loading: deleting}}
            >
                <p>Haqiqatan ham ushbu eventni o'chirmoqchimisiz?</p>
                <p className="text-danger">
                    <strong>Diqqat:</strong> Bu amal qaytarilmaydi va event bilan bog'liq barcha
                    topshiriqlar ham o'chiriladi.
                </p>
            </Modal>
        </Container>
    );
};

export default EventDetail;
